import { SearchOffOutlined as SearchOffIcon } from "@mui/icons-material";
import { Box, GridLegacy as Grid } from "@mui/material";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AdminEmptyState } from "@/components/common/AdminEmptyState";
import { LocalizedTablePagination } from "@/components/common/LocalizedTablePagination";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import { getReportNetworkLoading } from "@/hooks/getReportNetworkLoading";
import { useCantFindOccurrences } from "@/hooks/useCantFindOccurrences";
import { useCantFindRequests } from "@/hooks/useCantFindRequests";
import CantFindOccurrencesTable from "@/pages/reports/cant-find/CantFindOccurrencesTable";
import { ReportSummaryTable } from "@/pages/reports/shared/ReportSummaryTable";
import { StoreInsightsPanel } from "@/pages/reports/shared/StoreInsightsPanel";
import { StoreInsightsReportHeader } from "@/pages/reports/shared/StoreInsightsReportHeader";
import { downloadCsv } from "@/pages/reports/shared/storeInsightsCsv";
import { StoreInsightsSummaryCardsRow } from "@/pages/reports/shared/StoreInsightsSummaryCardsRow";
import { useTrendCardPresentation } from "@/pages/reports/shared/useTrendCardPresentation";
import { useStoreInsightsReportFilters } from "@/pages/reports/shared/useStoreInsightsReportFilters";
import { useStoreInsightsTrendComparison } from "@/pages/store-insights/shared/useStoreInsightsTrendComparison";
import { storePerformanceService } from "@/services/StorePerformanceService";
import {
  CurrencyCode,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import { formatInteger, formatPercentFromWhole } from "@/utils/number";
import {
  formatStoreDateRange,
  formatStoreDateTimeFileStamp,
  getBrowserTimezone,
  getInclusiveStoreDayCount,
  getStoreDayIndex,
} from "@/utils/storeTime";
import { formatDurationShort } from "@/utils/time";

interface UserSummaryRow {
  name: string;
  cantFindCount: number;
  totalValue: number;
  averageSearchTimeSeconds: number;
}

interface DaySummaryRow {
  day: string;
  dayIndex: number;
  cantFindCount: number;
  totalValue: number;
  averageSearchTimeSeconds: number;
}

type UserSummaryOrderKey = keyof UserSummaryRow;
type DaySummaryOrderKey = keyof Omit<DaySummaryRow, "dayIndex">;

const CantFindReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const {
    user,
    isCraveUser,
    selectedStores,
    loading,
    selectionLoading,
    period,
    customRange,
    currentDateRange,
    deviceType,
    customers,
    selectedCustomerIds,
    groups,
    selectedGroups,
    groupCounts,
    filteredStores,
    onCustomerChange,
    onGroupsChange,
    onStoresChange,
    onPeriodChange,
    onCustomRangeChange,
  } = useStoreInsightsReportFilters({ enableDeviceFilter: false });
  const {
    comparisonWindow,
    setComparisonWindow,
    anchorComparisonToWeekdays,
    setAnchorComparisonToWeekdays,
  } = useStoreInsightsTrendComparison(period, currentDateRange);
  const comparisonRange = useMemo(
    () =>
      storePerformanceService.getTrendComparisonRange(
        currentDateRange,
        comparisonWindow,
        browserTimezone,
        anchorComparisonToWeekdays,
      ),
    [anchorComparisonToWeekdays, browserTimezone, comparisonWindow, currentDateRange],
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState<UserSummaryOrderKey>("cantFindCount");
  const [dayOrder, setDayOrder] = useState<"asc" | "desc">("desc");
  const [dayOrderBy, setDayOrderBy] = useState<DaySummaryOrderKey>("cantFindCount");
  const [selectedAssociateName, setSelectedAssociateName] = useState<string | null>(null);
  const occurrencesRef = useRef<HTMLDivElement>(null);

  const cantFindRequestsData = useCantFindRequests(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
    comparisonRange,
  );
  const occurrences = useCantFindOccurrences(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
  );
  const networkLoading = getReportNetworkLoading(
    loading,
    selectionLoading,
    cantFindRequestsData.percentage.loading,
    cantFindRequestsData.monetary.loading,
    occurrences.loading,
  );
  const contentLoading = networkLoading;
  const displayedDayCount = useMemo(
    () => getInclusiveStoreDayCount(currentDateRange.start, currentDateRange.end, browserTimezone),
    [browserTimezone, currentDateRange.end, currentDateRange.start],
  );
  const loadingKey = useMemo(
    () =>
      [
        selectedStores.map((store) => store.id).join(","),
        currentDateRange.start.getTime(),
        currentDateRange.end.getTime(),
        deviceType,
      ].join("|"),
    [currentDateRange.end, currentDateRange.start, deviceType, selectedStores],
  );
  const trendDateRange = useMemo(() => {
    const range = formatStoreDateRange(
      comparisonRange.start,
      comparisonRange.end,
      browserTimezone,
      i18n.resolvedLanguage || i18n.language,
      { weekday: "short", month: "short", day: "numeric" },
    );
    return range.startDate === range.endDate
      ? range.startDate
      : `${range.startDate} – ${range.endDate}`;
  }, [
    browserTimezone,
    comparisonRange.end,
    comparisonRange.start,
    i18n.language,
    i18n.resolvedLanguage,
  ]);
  const trendProps = useTrendCardPresentation(trendDateRange);
  const storeTimezones = useMemo(
    () => Object.fromEntries(selectedStores.map((store) => [store.id, store.timezone])),
    [selectedStores],
  );

  const userSummary = useMemo<UserSummaryRow[]>(() => {
    const summary = new Map<
      string,
      { count: number; totalSearchTimeSeconds: number; totalValue: number }
    >();

    occurrences.data.forEach((row) => {
      const current = summary.get(row.associateName) ?? {
        count: 0,
        totalSearchTimeSeconds: 0,
        totalValue: 0,
      };
      current.count += 1;
      current.totalSearchTimeSeconds += row.searchTimeSeconds;
      current.totalValue += row.value ?? 0;
      summary.set(row.associateName, current);
    });

    return Array.from(summary.entries())
      .map(([name, value]) => ({
        name,
        cantFindCount: value.count,
        totalValue: value.totalValue,
        averageSearchTimeSeconds:
          value.count > 0 ? Math.round(value.totalSearchTimeSeconds / value.count) : 0,
      }))
      .sort((a, b) => {
        if (b.cantFindCount !== a.cantFindCount) {
          return b.cantFindCount - a.cantFindCount;
        }
        if (b.averageSearchTimeSeconds !== a.averageSearchTimeSeconds) {
          return b.averageSearchTimeSeconds - a.averageSearchTimeSeconds;
        }
        return a.name.localeCompare(b.name);
      });
  }, [occurrences.data]);

  const daySummary = useMemo<DaySummaryRow[]>(() => {
    const daysOfWeek = [
      t("storeInsights.common.days.sunday"),
      t("storeInsights.common.days.monday"),
      t("storeInsights.common.days.tuesday"),
      t("storeInsights.common.days.wednesday"),
      t("storeInsights.common.days.thursday"),
      t("storeInsights.common.days.friday"),
      t("storeInsights.common.days.saturday"),
    ];
    const summary = new Map<
      number,
      { count: number; totalSearchTimeSeconds: number; totalValue: number }
    >();

    occurrences.data.forEach((row) => {
      const dayIndex = getStoreDayIndex(row.createdAt, storeTimezones[row.storeId]);
      if (dayIndex === null) {
        return;
      }

      const current = summary.get(dayIndex) ?? {
        count: 0,
        totalSearchTimeSeconds: 0,
        totalValue: 0,
      };
      current.count += 1;
      current.totalSearchTimeSeconds += row.searchTimeSeconds;
      current.totalValue += row.value ?? 0;
      summary.set(dayIndex, current);
    });

    return daysOfWeek
      .map((day, dayIndex) => {
        const stats = summary.get(dayIndex) ?? {
          count: 0,
          totalSearchTimeSeconds: 0,
          totalValue: 0,
        };
        return {
          day,
          dayIndex,
          cantFindCount: stats.count,
          totalValue: stats.totalValue,
          averageSearchTimeSeconds:
            stats.count > 0 ? Math.round(stats.totalSearchTimeSeconds / stats.count) : 0,
        };
      })
      .filter((row) => row.cantFindCount > 0);
  }, [occurrences.data, storeTimezones, t]);

  const sortedUserSummary = useMemo(() => {
    return [...userSummary].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (bValue < aValue) {
        return order === "desc" ? -1 : 1;
      }
      if (bValue > aValue) {
        return order === "desc" ? 1 : -1;
      }
      return 0;
    });
  }, [userSummary, order, orderBy]);

  const sortedDaySummary = useMemo(() => {
    return [...daySummary].sort((a, b) => {
      if (dayOrderBy === "day") {
        // Sort by day of week index normally, or name?
        // Usually day of week sorting is by chronological order, but here we can follow standard table sorting
        const aValue = a.dayIndex;
        const bValue = b.dayIndex;
        return dayOrder === "desc" ? bValue - aValue : aValue - bValue;
      }

      const aValue = a[dayOrderBy as keyof DaySummaryRow];
      const bValue = b[dayOrderBy as keyof DaySummaryRow];

      if (typeof aValue === "number" && typeof bValue === "number") {
        if (bValue < aValue) return dayOrder === "desc" ? -1 : 1;
        if (bValue > aValue) return dayOrder === "desc" ? 1 : -1;
        return 0;
      }
      return 0;
    });
  }, [daySummary, dayOrder, dayOrderBy]);

  const maxPage = Math.max(Math.ceil(userSummary.length / rowsPerPage) - 1, 0);
  const currentPage = Math.min(page, maxPage);
  const filteredOccurrences = useMemo(() => {
    if (!selectedAssociateName) return occurrences.data;
    return occurrences.data.filter((row) => row.associateName === selectedAssociateName);
  }, [occurrences.data, selectedAssociateName]);

  const formatSearchTime = (seconds: number) =>
    formatDurationShort(seconds, i18n.resolvedLanguage || i18n.language);
  const currentLocale = useMemo(
    () => new Intl.Locale(i18n.resolvedLanguage || i18n.language || "en-US"),
    [i18n.resolvedLanguage, i18n.language],
  );

  const [currencyCode, currencySymbol] = useMemo(
    () => getStoreCurrencyIdentifiers(selectedStores),
    [selectedStores],
  );

  const formatRoundedAmount = (value: number) =>
    formatCurrencyWithNoDecimals(
      Math.round(value),
      currencyCode as CurrencyCode,
      currencySymbol,
      currentLocale,
    );

  const handleUserSort = (property: UserSummaryOrderKey) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleDaySort = (property: DaySummaryOrderKey) => {
    const isAsc = dayOrderBy === property && dayOrder === "asc";
    setDayOrder(isAsc ? "desc" : "asc");
    setDayOrderBy(property);
  };

  const handleAssociateRowClick = (name: string) => {
    setSelectedAssociateName((current) => {
      const next = current === name ? null : name;
      if (next !== null) {
        setTimeout(() => {
          occurrencesRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
      return next;
    });
  };

  const handleExportUserSummary = () => {
    if (userSummary.length === 0) return;

    downloadCsv(
      [
        [
          t("storeInsights.common.fields.associate"),
          t("storeInsights.cantFindReport.cards.cantFinds"),
          t("storeInsights.common.fields.value"),
          t("storeInsights.common.fields.avgSearchTime"),
        ],
        ...sortedUserSummary.map((row) => [
          row.name,
          row.cantFindCount,
          formatCurrencyWithNoDecimals(
            row.totalValue,
            currencyCode as CurrencyCode,
            currencySymbol,
            currentLocale,
          ),
          formatSearchTime(row.averageSearchTimeSeconds),
        ]),
      ],
      `users_export_${formatStoreDateTimeFileStamp(new Date(), browserTimezone)}.csv`,
    );
  };

  const handleExportDaySummary = () => {
    if (daySummary.length === 0) return;

    downloadCsv(
      [
        [
          t("storeInsights.common.fields.day"),
          t("storeInsights.cantFindReport.cards.cantFinds"),
          t("storeInsights.common.fields.value"),
          t("storeInsights.common.fields.avgSearchTime"),
        ],
        ...sortedDaySummary.map((row) => [
          row.day,
          row.cantFindCount,
          formatCurrencyWithNoDecimals(
            row.totalValue,
            currencyCode as CurrencyCode,
            currencySymbol,
            currentLocale,
          ),
          formatSearchTime(row.averageSearchTimeSeconds),
        ]),
      ],
      `day_export_${formatStoreDateTimeFileStamp(new Date(), browserTimezone)}.csv`,
    );
  };

  return (
    <Box sx={{ pb: 4 }}>
      <StoreInsightsReportHeader
        title={t("storeInsights.cantFindReport.title")}
        icon={<SearchOffIcon />}
        isCraveUser={isCraveUser}
        currentUserRole={user?.role}
        customers={customers}
        selectedCustomerIds={selectedCustomerIds}
        onCustomerChange={onCustomerChange}
        groups={groups}
        selectedGroups={selectedGroups}
        groupCounts={groupCounts}
        onGroupsChange={onGroupsChange}
        filteredStores={filteredStores}
        selectedStores={selectedStores}
        onStoresChange={onStoresChange}
        loading={loading}
        networkLoading={networkLoading}
        period={period}
        currentDateRange={currentDateRange}
        customRange={customRange}
        onPeriodChange={onPeriodChange}
        onCustomRangeChange={onCustomRangeChange}
        deviceTypeFilterEnabled
        deviceTypeFilterDisabled
        selectedDeviceType="all"
        showTrendSelector
        trendPeriod={comparisonWindow}
        onTrendPeriodChange={setComparisonWindow}
        trendComparisonRange={comparisonRange}
        anchorTrendComparisonToWeekdays={anchorComparisonToWeekdays}
        onAnchorTrendComparisonToWeekdaysChange={setAnchorComparisonToWeekdays}
      />

      <Box sx={{ p: { xs: 2, md: 2.5 }, pt: 0, mt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StoreInsightsSummaryCardsRow
              title={t("storeInsights.common.sections.requestsBreakdown")}
              cards={[
                {
                  value: cantFindRequestsData.percentage.value,
                  previousText: cantFindRequestsData.percentage.previousValue,
                  title: t("storeInsights.cantFindReport.cards.cantFinds"),
                  isPositive: cantFindRequestsData.percentage.isPositive,
                  subtitle: cantFindRequestsData.percentage.subtitle,
                  subtitleLoading: contentLoading,
                  pillText: cantFindRequestsData.percentage.pillText,
                  trend: trendProps(
                    cantFindRequestsData.percentageTrend,
                    (value, locale) => formatPercentFromWhole(value, locale),
                    cantFindRequestsData.percentageComparisonProgress,
                  ),
                },
                {
                  value: cantFindRequestsData.monetary.value,
                  previousText: cantFindRequestsData.monetary.previousValue,
                  title: t("storeInsights.cantFindReport.cards.cantFindValue"),
                  isPositive: cantFindRequestsData.monetary.isPositive,
                  subtitle: cantFindRequestsData.monetary.subtitle,
                  subtitleLoading: contentLoading,
                  pillText: cantFindRequestsData.monetary.pillText,
                  pillTextAsChip: true,
                  trend: trendProps(cantFindRequestsData.monetaryTrend, (value) =>
                    formatCurrencyWithNoDecimals(
                      value,
                      currencyCode as CurrencyCode,
                      currencySymbol,
                    ),
                  ),
                },
              ]}
            />
          </Grid>

          <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
            <StoreInsightsPanel
              title={t("storeInsights.common.sections.dayOfWeek")}
              onExport={handleExportDaySummary}
              exportDisabled={contentLoading || daySummary.length === 0}
            >
              {contentLoading ? (
                <Box sx={{ mb: 2.5 }}>
                  <LongRunningDataLoader
                    variant="table"
                    isLoading={true}
                    baseLabels={[
                      t("storeInsights.common.loading.daySummary"),
                      t("storeInsights.common.loading.groupingResults"),
                      t("storeInsights.common.loading.calculatingInsights"),
                      t("storeInsights.common.loading.preparingResults"),
                    ]}
                    loopingLabels={[
                      t("storeInsights.common.loading.stillWorking"),
                      t("storeInsights.common.loading.thanksForPatience"),
                    ]}
                    estimate={{
                      meanTimePerStoreMs: 1_500,
                      storeCount: selectedStores.length,
                      displayedDayCount,
                      growth: "logarithmic",
                      logarithmicLinearWeight: 0.1,
                    }}
                    locale={i18n.resolvedLanguage || i18n.language}
                    loadingKey={loadingKey}
                    estimateLabel={(duration) =>
                      t("storeComparison.loading.usuallyUnder", { duration })
                    }
                  />
                </Box>
              ) : null}
              <ReportSummaryTable<DaySummaryRow>
                resetKey={loadingKey}
                rows={sortedDaySummary}
                getRowId={(row) => row.dayIndex}
                loading={contentLoading}
                initialOrderBy={dayOrderBy}
                sort={{
                  orderBy: dayOrderBy,
                  order: dayOrder,
                  onSort: (id) => handleDaySort(id as DaySummaryOrderKey),
                }}
                columns={[
                  {
                    id: "day",
                    label: t("storeInsights.common.fields.day"),
                    mobileRole: "identity",
                    sortValue: (row) => row.dayIndex,
                    render: (row) => row.day,
                  },

                  {
                    id: "cantFindCount",
                    label: t("storeInsights.cantFindReport.cards.cantFinds"),
                    sortValue: (row) => row.cantFindCount,
                    render: (row) => formatInteger(row.cantFindCount, currentLocale),
                  },
                  {
                    id: "totalValue",
                    label: t("storeInsights.common.fields.value"),
                    sortValue: (row) => row.totalValue,
                    render: (row) => formatRoundedAmount(row.totalValue),
                  },
                  {
                    id: "averageSearchTimeSeconds",
                    label: t("storeInsights.common.fields.avgSearchTime"),
                    sortValue: (row) => row.averageSearchTimeSeconds,
                    render: (row) => formatSearchTime(row.averageSearchTimeSeconds),
                  },
                ]}
                emptyState={
                  <AdminEmptyState
                    icon={<SearchOffIcon sx={{ fontSize: 40 }} />}
                    text={t("storeInsights.common.empty.noDayData")}
                  />
                }
              />
            </StoreInsightsPanel>
          </Grid>

          <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
            <StoreInsightsPanel
              title={t("storeInsights.common.sections.associates")}
              onExport={handleExportUserSummary}
              exportDisabled={contentLoading || userSummary.length === 0}
              footer={
                <>
                  <LocalizedTablePagination
                    rowsPerPageOptions={[5, 10]}
                    component="div"
                    count={userSummary.length}
                    rowsPerPage={rowsPerPage}
                    page={currentPage}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    }}
                  />
                  <Box className="mt-2 flex items-center justify-center border-t border-gray-50 pt-2">
                    <Box
                      component="p"
                      className="bg-gradient-to-r from-[#0066ff] to-[#00B7FF] bg-clip-text pt-2 text-[11px] font-medium text-transparent"
                    >
                      {t("storeInsights.cantFindReport.text.recoveryInsightsComingSoon")}
                    </Box>
                  </Box>
                </>
              }
            >
              {contentLoading ? (
                <Box sx={{ mb: 2.5 }}>
                  <LongRunningDataLoader
                    variant="table"
                    isLoading={true}
                    baseLabels={[
                      t("storeInsights.common.loading.associateSummary"),
                      t("storeInsights.common.loading.matchingData"),
                      t("storeInsights.common.loading.groupingResults"),
                      t("storeInsights.common.loading.preparingResults"),
                    ]}
                    loopingLabels={[
                      t("storeInsights.common.loading.stillWorking"),
                      t("storeInsights.common.loading.thanksForPatience"),
                    ]}
                    estimate={{
                      meanTimePerStoreMs: 1_500,
                      storeCount: selectedStores.length,
                      displayedDayCount,
                      growth: "logarithmic",
                      logarithmicLinearWeight: 0.1,
                    }}
                    locale={i18n.resolvedLanguage || i18n.language}
                    loadingKey={loadingKey}
                    estimateLabel={(duration) =>
                      t("storeComparison.loading.usuallyUnder", { duration })
                    }
                  />
                </Box>
              ) : null}
              <ReportSummaryTable<UserSummaryRow>
                rows={sortedUserSummary.slice(
                  currentPage * rowsPerPage,
                  currentPage * rowsPerPage + rowsPerPage,
                )}
                getRowId={(row) => row.name}
                loading={contentLoading}
                initialOrderBy={orderBy}
                resetKey={`${loadingKey}:${currentPage}:${rowsPerPage}:${selectedAssociateName ?? ""}`}
                sort={{ orderBy, order, onSort: (id) => handleUserSort(id as UserSummaryOrderKey) }}
                onRowActivate={(row) => handleAssociateRowClick(row.name)}
                isRowSelected={(row) => selectedAssociateName === row.name}
                columns={[
                  {
                    id: "name",
                    label: t("storeInsights.common.fields.associate"),
                    mobileRole: "identity",
                    sortValue: (row) => row.name,
                    render: (row) => (
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: "primary.main",
                          textDecoration: selectedAssociateName === row.name ? "underline" : "none",
                        }}
                      >
                        {row.name}
                      </Box>
                    ),
                  },

                  {
                    id: "cantFindCount",
                    label: t("storeInsights.cantFindReport.cards.cantFinds"),
                    sortValue: (row) => row.cantFindCount,
                    render: (row) => formatInteger(row.cantFindCount, currentLocale),
                  },
                  {
                    id: "totalValue",
                    label: t("storeInsights.common.fields.value"),
                    sortValue: (row) => row.totalValue,
                    render: (row) => formatRoundedAmount(row.totalValue),
                  },
                  {
                    id: "averageSearchTimeSeconds",
                    label: t("storeInsights.common.fields.avgSearchTime"),
                    sortValue: (row) => row.averageSearchTimeSeconds,
                    render: (row) => formatSearchTime(row.averageSearchTimeSeconds),
                  },
                ]}
                emptyState={
                  <AdminEmptyState
                    icon={<SearchOffIcon sx={{ fontSize: 40 }} />}
                    text={t("storeInsights.common.empty.noAssociateData")}
                  />
                }
              />
            </StoreInsightsPanel>
          </Grid>

          <Grid item xs={12} ref={occurrencesRef}>
            <CantFindOccurrencesTable
              data={filteredOccurrences}
              loading={contentLoading}
              error={occurrences.error}
              storeTimezones={storeTimezones}
              associateFilter={selectedAssociateName}
              allAssociates={userSummary.map((u) => u.name)}
              onAssociateChange={(name) => setSelectedAssociateName(name)}
              onRequestImage={occurrences.loadImageForOccurrence}
              stores={selectedStores}
              loadingKey={loadingKey}
              displayedDayCount={displayedDayCount}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default CantFindReport;
